import { useState } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import * as Dialog from "@/components/dialog/DialogLayout";
import { Icon } from "@/components/icons/Icon";
import { useStore } from "@/store";
import { trackEvent } from "@/utils/trackEvent";
import { twMerge } from "@/utils/twMerge";
import { useIsMyDoc } from "@/utils/useIsMyDoc";
import { useIsPublicProfileDoc } from "@/utils/useIsPublicProfileDoc";

import { CopyLinkButtons } from "./components/CopyLinkButtons";
import { PublicAccessToggle } from "./components/PublicAccessToggle";
import { ReadWriteDropdown } from "./components/ReadWriteDropdown";
import { PermissionsContent } from "./content/PermissionsContent";
import { ShareContent } from "./content/ShareContent";

import type { DocumentModel } from "@/models/DocumentModel";

const PublicAccessTogglePanel = observer(
  ({ doc, setTab }: { doc: DocumentModel; setTab: (tab: "link" | "qr") => void }) => {
    const { publicRead } = doc;
    return (
      <div className="flex flex-col rounded-8 p-2.5 bg-gray-100 dark:bg-gray-900">
        <div className="flex flex-row items-center justify-between">
          <PublicAccessToggle doc={doc} />
          {publicRead && (
            <span data-testid="share-public-link-permission-dropdown">
              <ReadWriteDropdown doc={doc} />
            </span>
          )}
        </div>
        {publicRead && <CopyLinkButtons doc={doc} onQRCodeClick={() => setTab("qr")} />}
      </div>
    );
  },
);

const PublicProfilePanel = observer(function PublicProfilePanel({
  doc,
  setTab,
}: {
  doc: DocumentModel;
  setTab: (tab: "link" | "qr") => void;
}) {
  const t = useTranslations();
  return (
    <div className="flex flex-col rounded-8 p-2.5 bg-gray-100 dark:bg-gray-900">
      <div className="flex flex-row items-center">
        <Icon name="Globe" className="min-w-[24px] min-h-[24px] mt-1 mx-1.25 mb-auto" />
        <div
          className={twMerge(
            "max-w-[250px] min-h-[60px] mt-1.5 text-[16px] leading-[20px] font-geist tracking-tight",
            "sm:max-h-[200px]",
          )}
        >
          {t("share.publicProfilePanelTitle")}
        </div>
        <ReadWriteDropdown doc={doc} />
      </div>
      <CopyLinkButtons doc={doc} onQRCodeClick={() => setTab("qr")} />
    </div>
  );
});

export const ShareDialog = observer(function ShareDialog({ doc }: { doc: DocumentModel }) {
  const { dialog } = useStore();
  const { shareDialog } = dialog;
  const { isOpen } = shareDialog;
  const [tab, setTab] = useState<"link" | "qr">("link");
  const isMyDoc = useIsMyDoc(doc);
  const isPublicProfileDoc = useIsPublicProfileDoc(doc);

  const handleClose = () => {
    if (doc.id) {
      trackEvent("share_menu_closed", { doc_id: doc.id });
    }
    setTab("link");
    shareDialog.close();
  };

  return (
    <Dialog.Root open={isOpen} onClose={handleClose}>
      <Dialog.Backdrop />
      <Dialog.Container>
        <Dialog.Panel
          className={twMerge(
            "overflow-y-auto flex-col rounded-0 max-w-[430px] w-full max-h-full p-0 bg-transparent dark:bg-transparent",
            "sm:rounded-none sm:max-w-none sm:h-full",
          )}
        >
          <Dialog.CloseButton className="z-10 top-[14px] right-[14px] w-[30px] h-[30px] rounded-8 hover:bg-blue-200 dark:hover:bg-blue-800">
            <Dialog.CloseIcon className="w-[24px] h-[24px]" />
          </Dialog.CloseButton>
          <div
            className={twMerge(
              tab === "link" ? "max-h-[410px]" : "max-h-full",
              "relative flex flex-col rounded-12 p-2.5 bg-document-bg dark:bg-gray-800",
              "sm:rounded-none sm:max-w-none sm:max-h-none",
            )}
          >
            <div
              className={twMerge(
                "flex flex-1 flex-col min-h-0 rounded-8 p-2.5 bg-gray-100 dark:bg-gray-900 h-full sm:min-h-auto sm:h-auto sm:max-h-none",
              )}
            >
              <ShareContent doc={doc} tab={tab} />
              {tab === "link" && <PermissionsContent doc={doc} />}
            </div>
          </div>
          {isMyDoc && tab === "link" && !isPublicProfileDoc && (
            <div
              data-testid="share-public-access-panel"
              className={twMerge(
                "relative rounded-12 mt-2.5 p-2.5 bg-document-bg dark:bg-gray-800",
                "sm:rounded-none sm:mt-0",
              )}
            >
              <PublicAccessTogglePanel doc={doc} setTab={setTab} />
            </div>
          )}
          {isPublicProfileDoc && tab === "link" && (
            <div
              className={twMerge(
                "relative rounded-12 mt-2.5 p-2.5 bg-document-bg dark:bg-gray-800",
                "sm:rounded-none sm:mt-0",
              )}
            >
              <PublicProfilePanel doc={doc} setTab={setTab} />
            </div>
          )}
        </Dialog.Panel>
      </Dialog.Container>
    </Dialog.Root>
  );
});
